import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

export default function Modal({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    showSubmit = true,
    submitLabel = 'Aceptar',
    cancelLabel = 'Cancelar',
    onSubmit,
    loading = false,
    disabled = false,
    className = ''
}) {
    const modalRef = useRef();

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    if (!isOpen) return null;

    const sizeClasses = {
        sm: 'tw-max-w-md',
        md: 'tw-max-w-lg',
        lg: 'tw-max-w-2xl',
        xl: 'tw-max-w-4xl',
        full: 'tw-max-w-6xl'
    };

    return ReactDOM.createPortal(
        <div
            className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50 tw-backdrop-blur-sm tw-transition-opacity"
            onClick={handleBackdropClick}
        >
            <div
                ref={modalRef}
                className={`${sizeClasses[size]} tw-w-full tw-mx-4 tw-bg-white tw-rounded-t-xl tw-shadow-2xl tw-transform tw-transition-all ${className}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-200 tw-bg-gradient-to-r tw-from-purple-50 tw-to-white tw-rounded-t-xl">
                    <h3 className="tw-text-lg tw-font-semibold tw-text-purple-700">
                        {title}
                    </h3>
                    <button
                        onClick={onClose}
                        className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
                    >
                        <svg className="tw-w-5 tw-h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="tw-px-6 tw-py-4 tw-max-h-[calc(100vh-200px)] tw-overflow-y-auto">
                    {children}
                </div>

                {/* Footer  */}
                {(showSubmit || onClose) && (
                    <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-px-6 tw-py-4 tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-rounded-b-lg">
                        <button
                            onClick={onClose}
                            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-purple-500 tw-transition-colors"
                        >
                            {cancelLabel}
                        </button>
                        {showSubmit && (
                            <button
                                onClick={onSubmit}
                                disabled={loading || disabled}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-purple-600 tw-rounded-md hover:tw-bg-purple-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-purple-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                            >
                                {loading ? (
                                    <span className="tw-flex tw-items-center">
                                        <svg className="tw-animate-spin -tw-ml-1 tw-mr-2 tw-h-4 tw-w-4 tw-text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                        Procesando...
                                    </span>
                                ) : (
                                    submitLabel
                                )}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}